// Builds a polished, dawaChat-branded printable page from report data and opens the
// browser's print dialog (Save as PDF). Dependency-free — no PDF library needed.

const escapeHtml = value =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const BRAND = {
  navy: '#0C4A98',
  blue: '#6BBEF2',
  ink: '#1F2D3D',
  muted: '#64748B',
  line: '#E2E8F0',
  zebra: '#F5F8FC',
};

const buildStyles = () => `
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: 'Inter', 'Segoe UI', Arial, sans-serif; color: ${BRAND.ink}; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { padding: 28px 32px 60px; }
  .head { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid ${BRAND.navy}; padding-bottom: 16px; }
  .head img { height: 46px; }
  .head .meta { text-align: right; }
  .head .title { font-size: 20px; font-weight: 700; color: ${BRAND.navy}; margin: 0; }
  .head .sub { font-size: 12px; color: ${BRAND.muted}; margin-top: 4px; }
  .tiles { display: flex; flex-wrap: wrap; gap: 12px; margin: 20px 0; }
  .tile { flex: 1 1 160px; border: 1px solid ${BRAND.line}; border-top: 3px solid ${BRAND.blue}; border-radius: 8px; padding: 12px 14px; }
  .tile .label { font-size: 11px; color: ${BRAND.muted}; text-transform: uppercase; letter-spacing: .4px; }
  .tile .value { font-size: 20px; font-weight: 700; color: ${BRAND.navy}; margin-top: 4px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 11px; }
  thead th { background: ${BRAND.navy}; color: #fff; text-align: left; padding: 8px 10px; font-weight: 600; white-space: nowrap; }
  tbody td { padding: 7px 10px; border-bottom: 1px solid ${BRAND.line}; vertical-align: top; }
  tbody tr:nth-child(even) { background: ${BRAND.zebra}; }
  tbody tr { page-break-inside: avoid; }
  .footer { position: fixed; bottom: 16px; left: 32px; right: 32px; display: flex; justify-content: space-between; font-size: 10px; color: ${BRAND.muted}; border-top: 1px solid ${BRAND.line}; padding-top: 6px; }
  .empty { padding: 40px; text-align: center; color: ${BRAND.muted}; }
  @page { size: A4 landscape; margin: 14mm 10mm 18mm; }
`;

/**
 * @param {Object} opts
 * @param {string} opts.title       Report title (e.g. "Captain AI Report")
 * @param {string} [opts.dateRange] Human date range shown under the title
 * @param {Array<{label:string,value:(string|number)}>} [opts.summary] Optional summary tiles
 * @param {string[]} opts.headers   Column headers
 * @param {Array<Array<string|number>>} opts.rows  Row cells (strings), aligned to headers
 */
export const exportReportToPdf = ({ title, dateRange = '', summary = [], headers = [], rows = [] }) => {
  const win = window.open('', '_blank');
  if (!win) return false; // popup blocked

  const logoUrl = `${window.location.origin}/brand-assets/dawachat-logo.png`;
  const generatedAt = new Date().toLocaleString();

  const tiles = summary.length
    ? `<div class="tiles">${summary
        .map(
          t =>
            `<div class="tile"><div class="label">${escapeHtml(t.label)}</div><div class="value">${escapeHtml(t.value)}</div></div>`
        )
        .join('')}</div>`
    : '';

  const thead = `<tr>${headers.map(h => `<th>${escapeHtml(h)}</th>`).join('')}</tr>`;
  const tbody = rows.length
    ? rows.map(r => `<tr>${r.map(c => `<td>${escapeHtml(c)}</td>`).join('')}</tr>`).join('')
    : '';
  const tableOrEmpty = rows.length
    ? `<table><thead>${thead}</thead><tbody>${tbody}</tbody></table>`
    : '<div class="empty">No data for the selected range and filters.</div>';

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>${buildStyles()}</style></head>
  <body>
    <div class="page">
      <div class="head">
        <img src="${logoUrl}" alt="dawaChat" />
        <div class="meta">
          <p class="title">${escapeHtml(title)}</p>
          <p class="sub">${escapeHtml(dateRange)}${dateRange ? ' &middot; ' : ''}${rows.length} rows</p>
        </div>
      </div>
      ${tiles}
      ${tableOrEmpty}
      <div class="footer">
        <span>dawaChat &middot; www.dawatech.com</span>
        <span>Generated ${escapeHtml(generatedAt)}</span>
      </div>
    </div>
    <script>
      window.onload = function () { setTimeout(function () { window.focus(); window.print(); }, 350); };
    </script>
  </body></html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
  return true;
};
